<template>
  <div class="fraction-input-container">
    <!-- Main fraction input -->
    <up-textinput 
      v-model="fraction_text"
      :label="label"
      :labelIcon="labelIcon"
      :size="size"
      :font_size="font_size"
      :placeholder="placeholder || 'e.g., 3/4 or 2 1/3'"
      @input="handleInput"
    />
    
    <!-- Validation errors -->
    <div v-if="validation.errors.length > 0" class="fraction-errors">
      <small v-for="error in validation.errors" :key="error" class="error-text">
        {{ error }}
      </small>
    </div>
    
    <!-- Live preview of how fraction will render -->
    <div v-if="fraction_text && validation.isValid" class="fraction-preview">
      <small class="preview-label">Preview:</small>
      <div class="preview-display">
        <show-fractions :text="fraction_text" />
      </div>
    </div>
    
    <!-- Help text with examples -->
    <div v-if="showHelp" class="fraction-help">
      <small class="help-label">Examples:</small>
      <ul class="help-examples">
        <li v-for="example in examples" :key="example">
          <code>{{ example }}</code>
        </li>
      </ul>
    </div>
    
    <!-- Toggle help button -->
    <div class="fraction-controls">
      <button 
        type="button" 
        class="help-toggle"
        @click="showHelp = !showHelp"
      >
        {{ showHelp ? 'Hide' : 'Show' }} Examples
      </button>
    </div>
  </div>
</template>

<script>
import UpTextinput from "./UpTextinput.vue";
import ShowFractions from "../ShowFractions.vue";
import { 
  validateFractions, 
  getFractionExamples 
} from "@/utils/fractionRenderer";

export default {
  name: "fraction-input",
  components: {
    UpTextinput,
    ShowFractions,
  },
  props: {
    value: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "Fraction",
    },
    labelIcon: {
      type: String,
      default: "",
    },
    size: {
      type: String,
      default: "sm",
    },
    font_size: {
      type: Number,
      default: 13,
    },
    placeholder: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      fraction_text: this.value || "",
      showHelp: false,
      validation: { isValid: true, errors: [] },
      examples: getFractionExamples(),
    };
  },
  computed: {
    // No longer needed since ShowFractions handles rendering
  },
  methods: {
    handleInput() {
      // Validate input
      this.validation = validateFractions(this.fraction_text);
      
      // Emit the value
      this.$emit("input", this.fraction_text);
    },
  },
  watch: {
    value: {
      handler: function(new_val) {
        this.fraction_text = new_val || "";
        this.validation = validateFractions(this.fraction_text);
      },
    },
  },
};
</script>

<style scoped>
.fraction-input-container {
  position: relative;
}

.fraction-errors {
  margin-top: 4px;
}

.error-text {
  color: #ff3860;
  font-size: 11px;
  display: block;
  margin-bottom: 2px;
}

.fraction-preview {
  margin-top: 8px;
  padding: 8px;
  background-color: #f5f5f5;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.preview-label {
  font-weight: bold;
  color: #666;
  margin-bottom: 4px;
  display: block;
}

.preview-display {
  font-size: 16px;
  line-height: 1.5;
  min-height: 24px;
}

.fraction-help {
  margin-top: 8px;
  padding: 8px;
  background-color: #f0f8ff;
  border-radius: 4px;
  border: 1px solid #b3d9ff;
}

.help-label {
  font-weight: bold;
  color: #0066cc;
  margin-bottom: 4px;
  display: block;
}

.help-examples {
  margin: 0;
  padding-left: 16px;
}

.help-examples li {
  margin-bottom: 2px;
  font-size: 11px;
}

.help-examples code {
  background-color: #e6f3ff;
  padding: 1px 4px;
  border-radius: 2px;
  font-family: monospace;
}

.fraction-controls {
  margin-top: 4px;
}

.help-toggle {
  background: none;
  border: none;
  color: #0066cc;
  font-size: 11px;
  cursor: pointer;
  text-decoration: underline;
}

.help-toggle:hover {
  color: #004499;
}

/* Fraction styles are now handled by ShowFractions component */
</style>
